import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { login } from "@/lib/api";
import { toast } from "sonner";
import { Lock, User, Eye, EyeOff } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

export default function Login() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(username, password);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data));
      toast.success(t("login.success"));
      navigate("/");
    } catch (error) {
      toast.error(t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/939331/pexels-photo-939331.jpeg)' }}
      >
        <div className="absolute inset-0 bg-white/90" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div className="flex justify-center pt-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_equip-manager-26/artifacts/1ttifcr7_rt.png" 
              alt="Kaufmanns Consulting" 
              className="h-24 object-contain"
            />
          </div>
          <div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("login.heroTitle")}
            </h2>
            <p className="text-slate-600 text-lg max-w-md">
              {t("login.heroText")}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="login-card">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex justify-end mb-2">
              <LanguageSelector />
            </div>
            <div className="flex items-center justify-center mb-4 p-4 bg-white border border-slate-100 rounded-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_equip-manager-26/artifacts/1ttifcr7_rt.png" 
                alt="Kaufmanns Consulting" 
                className="h-12 object-contain"
              />
            </div>
            <CardTitle className="text-xl font-bold tracking-tight text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("login.title")}
            </CardTitle>
            <p className="text-slate-600 text-center">
              {t("login.subtitle")}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-700 font-medium">
                  {t("login.username")}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="admin"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="username-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  {t("login.password")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    data-testid="password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    aria-label={showPassword ? "Passwort verstecken" : "Passwort anzeigen"}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base font-medium bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? t("login.loading") : t("login.submit")}
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                Admin: a.ben@kaufmanns-consulting.com
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
